import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { formatNumber } from "@/lib/i18nUtils";
import { useTranslation } from "react-i18next";

interface StandardPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  itemLabelKey?: string;
  loading?: boolean;
}

export function StandardPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  itemLabelKey = "pagination.items",
  loading = false
}: StandardPaginationProps) {
  const { settings } = useLocale();
  const { t } = useTranslation('common');
  
  if (totalPages <= 1) return null;

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <div className="flex items-center justify-between mt-6">
      <p className="text-sm text-muted-foreground">
        {t('pagination.showing', { defaultValue: 'Showing' })} {formatNumber(startIndex + 1, settings.locale)}-{formatNumber(endIndex, settings.locale)} {t('pagination.of', { defaultValue: 'of' })} {formatNumber(totalItems, settings.locale)} {t(itemLabelKey)}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousPage}
          disabled={currentPage === 1 || loading}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('pagination.previous_label', { defaultValue: 'Previous' })}
        </Button>
        <div className="text-sm">
          {t('pagination.page', { defaultValue: 'Page' })} {formatNumber(currentPage, settings.locale)} {t('pagination.of', { defaultValue: 'of' })} {formatNumber(totalPages, settings.locale)}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNextPage}
          disabled={currentPage === totalPages || loading}
        >
          {t('pagination.next_label', { defaultValue: 'Next' })}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
